import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Package,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  Factory,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { ProductStockInfo } from '../../types/companyProfile';

interface ProductStockInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: ProductStockInfo;
  onSave: (data: ProductStockInfo) => void;
}

const PRODUCTION_TYPES = [
  {
    value: 'manufacturer',
    label: 'Üretici',
    description: 'Kendi üretimimizi yapıyoruz',
    icon: Factory,
    color: 'purple'
  },
  {
    value: 'reseller',
    label: 'Satıcı',
    description: 'Tedarikçilerden alıp satıyoruz',
    icon: Package,
    color: 'blue'
  },
  {
    value: 'both',
    label: 'Her İkisi',
    description: 'Hem üretim hem de satış yapıyoruz',
    icon: Factory,
    color: 'green'
  }
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Ürün analizi başlıyor...', subtext: 'Tedarik zincirinizi anlıyoruz' },
  { threshold: 2, message: 'AI stok modeli oluşturuyor...', subtext: 'Optimum stok seviyeleri hesaplanıyor' },
  { threshold: 3, message: 'Mükemmel! Veriler işleniyor...', subtext: 'Stok önerileri için hazırız' }
];

const ProductStockInfoModal: React.FC<ProductStockInfoModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [formData, setFormData] = useState<ProductStockInfo>(initialData || {});
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.productionType) count++;
    if (formData.averageLeadTime) count++;
    if (formData.minStockPolicy) count++;
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const handleInputChange = (field: keyof ProductStockInfo, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-violet-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Ürün & Stok Bilgileri</h2>
                <p className="text-sm text-gray-400">Tedarik ve stok bilgileriniz</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-violet-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 3</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[450px] overflow-y-auto">
          <div className="space-y-6">
            {/* Info Box */}
            <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
              <p className="text-sm text-purple-300">
                💡 <strong>Not:</strong> Toplam ürün sayısı (SKU), ana kategoriler, iade oranı gibi bilgileri mağaza bağlantılarınızdan otomatik olarak çekiyoruz.
              </p>
            </div>

            {/* Production Type */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                <Factory className="w-4 h-4 text-purple-400" />
                Üretim Tipi
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PRODUCTION_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.productionType === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleInputChange('productionType', type.value)}
                      className={`p-4 rounded-xl text-left transition-all ${
                        isSelected
                          ? `bg-${type.color}-500/20 border-2 border-${type.color}-500`
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isSelected ? `bg-${type.color}-500/20` : 'bg-white/5'
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            isSelected ? `text-${type.color}-400` : 'text-gray-500'
                          }`} />
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-green-400 ml-auto" />}
                      </div>
                      <p className={`font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Average Lead Time */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <Clock className="w-4 h-4 text-purple-400" />
                Ortalama Ürün Temin Süresi (Gün)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.averageLeadTime || ''}
                  onChange={(e) => handleInputChange('averageLeadTime', parseInt(e.target.value) || undefined)}
                  placeholder="Örn: 7"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">gün</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tedarikçinizden sipariş verdiğinizde ürünün size ulaşma süresi (ortalama)
              </p>
            </div>

            {/* Min Stock Policy */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Minimum Stok Politikası
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  value={formData.minStockPolicy || ''}
                  onChange={(e) => handleInputChange('minStockPolicy', parseInt(e.target.value) || undefined)}
                  placeholder="Örn: 5"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">adet</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Bir ürünün stok seviyesi bu sayının altına düştüğünde uyarı almak istersiniz
              </p>
            </div>

            {/* Helper Info */}
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <h4 className="text-sm font-medium text-white mb-2">🤖 AI Ne Öğreniyor?</h4>
              <ul className="space-y-1.5 text-xs text-gray-400">
                <li>• Üretici iseniz maliyet yapınızı daha iyi anlayabiliriz</li>
                <li>• Temin süresine göre stok uyarılarını optimize ederiz</li>
                <li>• Minimum stok politikanıza göre kritik ürün uyarıları gönderebiliriz</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {filledCount === 3 ? (
              <span className="text-green-400">✓ Tüm alanlar dolduruldu</span>
            ) : (
              <span>{3 - filledCount} alan kaldı</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Check className="w-4 h-4" />
              Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProductStockInfoModal;
